import { IFormControl } from '../../../../ui/forms/interfaces';
import { scrollToFirstErrorControl, validateControl, validateForm } from '../../../../domain/validation/validation';
import store, { billingAddressActions, rootActions, shippingAddressActions } from '../data/store';
import { Dispatch } from 'react';
import { CANADA_ID, USA_ID } from '../../../../domain/addresses/addresses-provider';
import { TaxIdStatusEnum } from '../enums/tax-id-status-enum';

/**
 * in case of errors in 'add new card' form --->
 * scroll to the top of the form
 */
export const scrollToAddNewCardForm = () => {

    const $el = document.getElementById('nameOnCard');
    if(!$el) return;

    const $header = document.getElementById('site-header');

    const fixed = !!$header ? $header.offsetHeight : 0;
    const top = $el.getBoundingClientRect().top;

    window.scrollTo({
        top: top + fixed
    });

    $el.scrollIntoView(true);
};

export const validateReferCredit = (referCreditControl: IFormControl, dispatch: Function) => {
    const errorControls: IFormControl[] = [];

    const isReferValid = validateForm([
        [
            referCreditControl,
            (updatedControl: IFormControl) => {
                dispatch(
                    rootActions.setControl({
                        controlName: 'referCreditControl',
                        control: validateControl(updatedControl),
                    })
                )
            }
        ],
    ], errorControls);

    if(!isReferValid){
        scrollToFirstErrorControl(errorControls);
    }

    return isReferValid;
};

/**
 * confirm order validation
 * billing form fields
 */
export const validateBillingAddress = (billingFormFields: [IFormControl, string][], dispatch: Function) => {

    const billingAddressDispatch = (updatedControl: IFormControl, controlName: string) => {
        dispatch(
            billingAddressActions.setControl({
                controlName,
                control: validateControl(updatedControl),
            })
        );
    };

    const formParams = billingFormFields.map(param => [
        param[0],
        (updatedControl: IFormControl) => billingAddressDispatch(updatedControl, param[1])
    ]) as  ([IFormControl, Dispatch<any>])[];

    const billingAddressErrorControls: IFormControl[] = [];
    const isBillingFormValidValid = validateForm(formParams, billingAddressErrorControls);

    if(!isBillingFormValidValid){
        scrollToFirstErrorControl(billingAddressErrorControls);
    }

    return isBillingFormValidValid;
};

/**
 * confirm order validation
 * shipping form fields
 */
export const validateShippingAddress = (shippingFormFields: [IFormControl, string][], dispatch: Function) => {

    const shippingAddressDispatch = (updatedControl: IFormControl, controlName: string) => {
        dispatch(
            shippingAddressActions.setControl({
                controlName,
                control: validateControl(updatedControl),
            })
        );
    };

    const formParams = shippingFormFields.map(param => [
        param[0],
        (updatedControl: IFormControl) => shippingAddressDispatch(updatedControl, param[1])
    ]) as  ([IFormControl, Dispatch<any>])[];

    const shippingAddressErrorControls: IFormControl[] = [];
    const isShippingFormValidValid = validateForm(formParams, shippingAddressErrorControls);

    if(!isShippingFormValidValid){
        scrollToFirstErrorControl(shippingAddressErrorControls);
    }

    return isShippingFormValidValid;
};

export const isCountryRequired = (control?: IFormControl) => {
    let isValid = !!control?.value;

    const reduxStore = store.getState();
    if(!reduxStore || !reduxStore.root.cartData || !reduxStore.root.cartData.cartItems) return isValid;

    const hasDigigrid = reduxStore.root.cartData.cartItems.find(cartItem => cartItem.skuShippingUSCanada && cartItem.isActive);
    const countryID = Number(control?.value) || 0;
    if(hasDigigrid && countryID !== USA_ID && countryID !== CANADA_ID){
        return false;
    }

    return isValid;
};

/**
 * the state is required when selected country has states
 */
export const isBillingStateRequired = (control?: IFormControl) => {
    const reduxStore = store.getState();
    if(!reduxStore || !reduxStore.billingAddress || !reduxStore.root || !reduxStore.root.cartData) return true;

    const countries = reduxStore.root.countries;
    if(!countries) return true;

    const selectedCountryId = Number(reduxStore.billingAddress.countryIdControl.value) || 0;
    if(!selectedCountryId) return true;

    const selectedCountry = countries.find(country => country.countryID.toString() === selectedCountryId.toString());
    const countryHasStates = selectedCountry && selectedCountry.states && selectedCountry.states.length > 0;
    if(!countryHasStates) return true;

    const controlStateId = Number(control?.value) || 0;

    // digigrid products can have usa / canada countries only
    const cartData = reduxStore.root.cartData;
    const hasDigigridItem = cartData.cartItems.find(cartItem => cartItem.skuShippingUSCanada && cartItem.isActive);
    if(hasDigigridItem){
        if(selectedCountryId !== USA_ID && selectedCountryId !== CANADA_ID){
            return false;
        }

        const found = selectedCountry.states.find(state => state.stateID === controlStateId);
        if(!found) {
            return false;
        }
    }

    return countryHasStates && !!controlStateId;
};


/**
 * the state is required when selected country has states
 */
export const isShippingStateRequired = (control?: IFormControl) => {
    const reduxStore = store.getState();
    if(!reduxStore || !reduxStore.shippingAddress || !reduxStore.root || !reduxStore.root.cartData) return true;

    const countries = reduxStore.root.countries;
    if(!countries) return true;

    const selectedCountryId = Number(reduxStore.shippingAddress.countryIdControl.value) || 0;
    if(!selectedCountryId) return true;

    const selectedCountry = countries.find(country => country.countryID.toString() === selectedCountryId.toString());
    const countryHasStates = selectedCountry && selectedCountry.states && selectedCountry.states.length > 0;
    if(!countryHasStates) return true;

    const controlStateId = Number(control?.value) || 0;

    // digigrid products can have usa / canada countries only
    const cartData = reduxStore.root.cartData;
    const hasDigigridItem = cartData.cartItems.find(cartItem => cartItem.skuShippingUSCanada && cartItem.isActive);
    if(hasDigigridItem){
        if(selectedCountryId !== USA_ID && selectedCountryId !== CANADA_ID){
            return false;
        }

        const found = selectedCountry.states.find(state => state.stateID === controlStateId);
        if(!found) {
            return false;
        }
    }

    return countryHasStates && !!controlStateId;
};

// ---------------- TAX ID VALIDATIONS ---------------------------

export const isTaxIdRequired = (control?: IFormControl) => {
    const reduxStore = store.getState();
    if(!reduxStore || !reduxStore.billingAddress) return true;

    const countries = reduxStore.root.countries;
    if(!countries) return true;

    const selectedCountryId = Number(reduxStore.billingAddress.countryIdControl.value) || 0;
    if(!selectedCountryId) return true;

    const selectedCountry = countries.find(country => country.countryID.toString() === selectedCountryId.toString());
    const taxIdRequired = selectedCountry && selectedCountry.isTaxID;
    if(!taxIdRequired) return true;

    const taxIdSelectedStatus = reduxStore.billingAddress.taxIdSelectedStatusControl;
    if(taxIdSelectedStatus && (Number(taxIdSelectedStatus.value) === TaxIdStatusEnum.NO)) return true;

    const value = control?.value || '';
    return !!value;
};

export const getTaxIdMinLength = () : number => {
    const reduxStore = store.getState();
    if(!reduxStore || !reduxStore.billingAddress) return 0;

    const countries = reduxStore.root.countries;
    if(!countries) return 0;

    const selectedCountryId = Number(reduxStore.billingAddress.countryIdControl.value) || 0;
    if(!selectedCountryId) return 0;

    const selectedCountry = countries.find(country => country.countryID.toString() === selectedCountryId.toString());
    const taxIdRequired = selectedCountry && selectedCountry.isTaxID;
    if(!taxIdRequired) return 0;

    const min = Number(selectedCountry.taxIDMinLength) || 0;
    if(min <= 0) return 0;

    return min;
};

export const getTaxIdMaxLength = () : number => {
    const reduxStore = store.getState();
    if(!reduxStore || !reduxStore.billingAddress) return Infinity;

    const countries = reduxStore.root.countries;
    if(!countries) return Infinity;

    const selectedCountryId = Number(reduxStore.billingAddress.countryIdControl.value) || 0;
    if(!selectedCountryId) return Infinity;

    const selectedCountry = countries.find(country => country.countryID.toString() === selectedCountryId.toString());
    const taxIdRequired = selectedCountry && selectedCountry.isTaxID;
    if(!taxIdRequired) return Infinity;

    const max = Number(selectedCountry.taxIDMaxLength) || 0;
    if(max <= 0) return Infinity;

    return max;
};

export const isTaxIdSelectedStatusRequired = (control?: IFormControl) => {
    const reduxStore = store.getState();
    if(!reduxStore || !reduxStore.billingAddress) return true;

    const countries = reduxStore.root.countries;
    if(!countries) return true;

    const selectedCountryId = Number(reduxStore.billingAddress.countryIdControl.value) || 0;
    if(!selectedCountryId) return true;

    const selectedCountry = countries.find(country => country.countryID.toString() === selectedCountryId.toString());
    const taxIdRequired = selectedCountry && selectedCountry.isTaxID;
    if(!taxIdRequired) return true;

    const taxIdSelectedStatus = Number(control?.value) || 0;
    return taxIdRequired && !!taxIdSelectedStatus;
};


/**
 * scroll to the element with the specified path
 */
export const scrollToPath = (path: string) => {
    const $el = document.querySelector(path);
    if(!$el) return;

    $el.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
    });
};
